import type { ComponentCheck } from '../hooks/useTierEvaluation';

/**
 * Local UI shell preview (Pavilion rewards, `local` branch only).
 *
 * Set to a tier id — e.g. `'tier-0'` — to bypass PlayJector / machine tier API and
 * show that assignment on Welcome, Hub, and About Tiers. Set to `null` when you want
 * real tier resolution again.
 */
/** Set to `'tier-0'` (etc.) only when you want fake tier data without a real machine assignment. */
export const LOCAL_SHELL_TIER_OVERRIDE: string | null = null;

/** When using API tier definitions locally, simulate the highest tier instead of the lowest. */
export const LOCAL_TEST_AS_MAX_TIER = false;

interface ShellTierDefinition {
  tierId: string;
  name: string;
  priority: number;
  canEarn: boolean;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  detailSummary?: string;
  requirements?: Record<string, { min?: number }>;
}

/** Minimal tier ladder for shell work when admin tiers API is unavailable. */
const SHELL_TIER_DEFINITIONS: ShellTierDefinition[] = [
  {
    tierId: 'tier-0',
    name: 'Blocked',
    priority: 0,
    canEarn: false,
    earnRateDescription: 'This machine cannot earn tokens on the network.',
    maxEarningsDescription: 'Resolve the issues below to unlock Tier 1.',
    detailSummary: 'Tier 0 — shell placeholder copy',
    requirements: { environment: { min: 50 } },
  },
  {
    tierId: 'tier-1',
    name: 'Tier 1',
    priority: 1,
    canEarn: true,
    earnRateDescription: 'Earn 1 point per hour',
    maxEarningsDescription: 'Earn up to $40 in Steam cards per month!',
  },
  {
    tierId: 'tier-2',
    name: 'Tier 2',
    priority: 2,
    canEarn: true,
    earnRateDescription: 'Earn 2 points per hour',
    maxEarningsDescription: 'Earn up to $80 in Steam cards per month!',
    requirements: { cpu: { min: 80 }, gpu: { min: 60 } },
  },
];

const COMPONENT_LABELS: Record<string, string> = {
  cpu: 'CPU',
  gpu: 'GPU',
  memory: 'Memory',
  storage: 'Storage',
  network: 'Network',
  videoEncoding: 'Video Encoding',
  environment: 'Environment',
};

function componentChecksFromRequirements(
  requirements: Record<string, { min?: number }> | undefined,
  passes: boolean,
): ComponentCheck[] {
  if (!requirements) return [];
  const checks: ComponentCheck[] = [];
  for (const [key, req] of Object.entries(requirements)) {
    if (req?.min && req.min > 0) {
      checks.push({
        component: key,
        label: COMPONENT_LABELS[key] ?? key,
        currentValue: 'N/A (local shell)',
        score: passes ? req.min : 0,
        requiredScore: req.min,
        passes,
      });
    }
  }
  return checks;
}

export interface SimulatedTierAssignment {
  tier: {
    tierId: string;
    name: string;
    canEarn: boolean;
    priority: number;
    earnRateDescription?: string;
    maxEarningsDescription?: string;
    detailSummary?: string;
    componentChecks: ComponentCheck[];
  };
  nextTier: {
    tierId: string;
    name: string;
    earnRateDescription?: string;
    maxEarningsDescription?: string;
    componentChecks: ComponentCheck[];
  } | null;
  previousTier: {
    tierId: string;
    name: string;
    earnRateDescription?: string;
    maxEarningsDescription?: string;
  } | null;
}

export function buildSimulatedTierAssignment(assignedTierId: string): SimulatedTierAssignment {
  const sorted = [...SHELL_TIER_DEFINITIONS].sort((a, b) => a.priority - b.priority);
  const assigned =
    sorted.find((t) => t.tierId === assignedTierId) ?? sorted[0];

  const higher = sorted.filter((t) => t.priority > assigned.priority);
  const lower = sorted.filter((t) => t.priority < assigned.priority);
  const nextDef = higher[0] ?? null;
  const prevDef = lower.length > 0 ? lower[lower.length - 1] : null;

  const tier = {
    tierId: assigned.tierId,
    name: assigned.name,
    canEarn: assigned.canEarn,
    priority: assigned.priority,
    earnRateDescription: assigned.earnRateDescription,
    maxEarningsDescription: assigned.maxEarningsDescription,
    detailSummary: assigned.detailSummary,
    componentChecks: componentChecksFromRequirements(assigned.requirements, true),
  };

  const nextTier = nextDef
    ? {
        tierId: nextDef.tierId,
        name: nextDef.name,
        earnRateDescription: nextDef.earnRateDescription,
        maxEarningsDescription: nextDef.maxEarningsDescription,
        componentChecks: componentChecksFromRequirements(nextDef.requirements, false),
      }
    : null;

  const previousTier = prevDef
    ? {
        tierId: prevDef.tierId,
        name: prevDef.name,
        earnRateDescription: prevDef.earnRateDescription,
        maxEarningsDescription: prevDef.maxEarningsDescription,
      }
    : null;

  return { tier, nextTier, previousTier };
}

export function getShellTierDefinitionsForLocalParse(): ShellTierDefinition[] {
  return SHELL_TIER_DEFINITIONS;
}

export type ShellTierDisplayInfo = {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
};

export function getShellTierDisplayInfo(tierId: string): ShellTierDisplayInfo | null {
  const def = SHELL_TIER_DEFINITIONS.find((t) => t.tierId === tierId);
  if (!def) return null;
  return {
    tierId: def.tierId,
    name: def.name,
    earnRateDescription: def.earnRateDescription,
    maxEarningsDescription: def.maxEarningsDescription,
  };
}
